/**
 * Swiper Product Slider Controller
 * Initializes Swiper instances and runs the dynamic [timer] replacement countdown.
 */

class SwiperProductSlider {
  constructor(section) {
    this.section = section;
    this.swiperEl = section.querySelector('.swiper-product-section__swiper');
    this.countdownEl = section.querySelector('.swiper-countdown-banner__message');
    
    this.initSwiper();
    this.initCountdown();
  }

  initSwiper() {
    if (!this.swiperEl) return;

    // Extract options from data attributes with robust defaults
    const autoplay = this.swiperEl.dataset.autoplay === 'true';
    const delay = parseInt(this.swiperEl.dataset.autoplayDelay) || 3000;
    const slidesDesktop = parseInt(this.swiperEl.dataset.slidesDesktop) || 4;
    const slidesMobile = parseInt(this.swiperEl.dataset.slidesMobile) || 1;
    const spaceBetween = parseInt(this.swiperEl.dataset.spaceBetween) || 24;
    const loop = this.swiperEl.dataset.loop === 'true';

    const swiperConfig = {
      slidesPerView: slidesMobile,
      spaceBetween: 16,
      loop: loop,
      autoplay: autoplay ? {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      pagination: {
        el: this.section.querySelector('.swiper-product-section__pagination'),
        clickable: true,
        dynamicBullets: true
      },
      navigation: {
        nextEl: this.section.querySelector('.swiper-product-section__button-next'),
        prevEl: this.section.querySelector('.swiper-product-section__button-prev')
      },
      breakpoints: {
        640: {
          slidesPerView: Math.min(2, slidesDesktop),
          spaceBetween: spaceBetween
        },
        768: {
          slidesPerView: Math.max(2, slidesDesktop - 1),
          spaceBetween: spaceBetween
        },
        1024: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceBetween
        }
      }
    };

    // Instantiate Swiper
    if (typeof Swiper !== 'undefined') {
      this.swiper = new Swiper(this.swiperEl, swiperConfig);
    } else {
      console.error('Swiper library not loaded. Check index.html CDNs.');
    }
  }

  initCountdown() {
    if (!this.countdownEl) return;

    const endDateStr = this.countdownEl.dataset.endDate;
    const templateText = this.countdownEl.dataset.templateText || "Sale will end soon in [timer]";
    
    // Parse target date
    let targetDate = new Date(endDateStr);
    // Fallback: If date is invalid or in the past, set target to 24h from now
    if (isNaN(targetDate.getTime()) || targetDate <= new Date()) {
      targetDate = new Date();
      targetDate.setHours(targetDate.getHours() + 24); // 24 hours from now
    }

    // Set up placeholder container inside message
    if (templateText.includes('[timer]')) {
      const parts = templateText.split('[timer]');
      this.countdownEl.innerHTML = `
        ${parts[0]}
        <span class="swiper-countdown-timer-placeholder"></span>
        ${parts[1] || ''}
      `;
    }

    const placeholder = this.countdownEl.querySelector('.swiper-countdown-timer-placeholder');
    if (!placeholder) return;

    const updateTimer = () => {
      const now = new Date();
      const difference = targetDate - now;

      if (difference <= 0) {
        placeholder.innerHTML = `
          <span class="swiper-countdown-timer-pill">
            <span class="swiper-countdown-timer-pill__icon-wrapper">
              <svg class="swiper-countdown-timer-pill__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1-11V7h-2v5.27l4.5 2.7.75-1.23-3.25-1.74zM15 1H9v2h6V1z"/>
              </svg>
            </span>
            <span class="swiper-countdown-timer-pill__digits">00:00:00</span>
          </span>
        `;
        return;
      }

      const totalSeconds = Math.floor(difference / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let timeString = "";
      if (days > 0) {
        timeString += String(days).padStart(2, '0') + ':';
      }
      timeString += String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':' + String(seconds).padStart(2, '0');

      placeholder.innerHTML = `
        <span class="swiper-countdown-timer-pill">
          <span class="swiper-countdown-timer-pill__icon-wrapper">
            <svg class="swiper-countdown-timer-pill__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 20c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-18C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1-11V7h-2v5.27l4.5 2.7.75-1.23-3.25-1.74zM15 1H9v2h6V1z"/>
            </svg>
          </span>
          <span class="swiper-countdown-timer-pill__digits">${timeString}</span>
        </span>
      `;
    };

    updateTimer();
    this.timerInterval = setInterval(updateTimer, 1000);
  }
}

// Instantiate product swiper sliders on DOM load and Shopify section load
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.swiper-product-section').forEach(section => {
    new SwiperProductSlider(section);
  });
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.swiper-product-section');
  if (section) {
    new SwiperProductSlider(section);
  }
});
